import React, { useState } from 'react';
import styles from './add-partial-payment-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ReactDatePicker from 'react-datepicker';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import 'react-dropzone-uploader/dist/styles.css';
import Dropzone from 'react-dropzone-uploader';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';

export interface AddPartialPaymentModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    paymentId?: string;
    currency?: string;
    totalAmount?: number;
  };

  eventHandlers: {
    closeHandler: () => void;
    addPartialPaymentHandler: (
      receipt: File,
      partialAmount: number,
      collectedAtDate: Date,
      paymentId?: string,
      skipEmail?: boolean
    ) => void;
  };
}

/**
 * functional component AddPartialPaymentModal
 * @param {AddPartialPaymentModalProps}
 */
export const AddPartialPaymentModal: React.FC<AddPartialPaymentModalProps> = ({
  config,
  eventHandlers,
  data
}) => {
  const [newReceipt, setNewReceipt] = useState<File>();
  const [skipEmail, setSkipEmail] = useState<boolean>(false);
  const [collectedAtDate, setCollectedAtDate] = useState<Date>(new Date());
  const [partialAmount, setPartialAmount] = useState<number>(0);

  const isMobileView = useWindowResizer();

  const handleAddPartialPaymentSubmit = () => {
    if (newReceipt && partialAmount > 0 && data.paymentId) {
      eventHandlers.addPartialPaymentHandler(
        newReceipt,
        partialAmount,
        collectedAtDate,
        data.paymentId,
        skipEmail
      );
    }
  };

  const handleSkipEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkipEmail(event.target.checked);
  };

  const handlePartialAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value) || 0;
    if (data.totalAmount && value <= data.totalAmount) {
      setPartialAmount(value);
    } else if (!data.totalAmount) {
      setPartialAmount(value);
    }
  };

  const isValidAmount =
    partialAmount > 0 && (!data.totalAmount || partialAmount <= data.totalAmount);

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['add-partial-payment-modal']}>
        <div className={styles['add-partial-payment-modal__title']}>
          Add Partial Payment Receipt
        </div>

        <div className={styles['add-partial-payment-modal__description']}>
          <div className={styles['add-partial-payment-modal__amount-container']}>
            <label htmlFor="partial-amount">Partial Payment Amount:</label>
            <div className={styles['add-partial-payment-modal__amount-input-wrapper']}>
              <span className={styles['add-partial-payment-modal__currency']}>
                {data.currency ? CURRENCY[data.currency] || 'EGP' : 'EGP'}
              </span>
              <input
                id="partial-amount"
                type="number"
                value={partialAmount}
                onChange={handlePartialAmountChange}
                placeholder="Enter amount"
                step="0.01"
                min="0"
                className={styles['add-partial-payment-modal__amount-input']}
              />
            </div>
            {data.totalAmount && (
              <small className={styles['add-partial-payment-modal__helper-text']}>
                Total amount: {data.currency ? CURRENCY[data.currency] || 'EGP' : 'EGP'}{' '}
                {data.totalAmount}
              </small>
            )}
          </div>
        </div>

        <div className={styles['add-partial-payment-modal__description']}>
          Collected At:
          <ReactDatePicker
            selected={collectedAtDate}
            onChange={(date) => setCollectedAtDate(date as Date)}
          />
        </div>

        <Dropzone
          classNames={{ input: styles['add-partial-payment-modal__dropzone'] }}
          inputContent={() => (
            <div className={styles['add-partial-payment-modal__upload-container']}>
              <CloudUploadIcon fontSize={'large'} />
              Upload proof of payment
            </div>
          )}
          accept="*"
          maxFiles={1}
          multiple={false}
          onChangeStatus={(file) => {
            setNewReceipt(file.file);
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={skipEmail}
              onChange={handleSkipEmailChange}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          }
          label="Do NOT send email to guest"
        />

        <div className={styles['add-partial-payment-modal--actions']}>
          <Button
            onClick={() => {
              eventHandlers.closeHandler();
            }}
            type={ButtonType.primaryInverted}
            tracking={{
              action: 'close',
              category: 'payments',
              label: 'add-partial-payment-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            disabled={!newReceipt || !isValidAmount}
            onClick={handleAddPartialPaymentSubmit}
            tracking={{
              action: 'submit',
              category: 'payment',
              label: 'add-partial-payment-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Add Partial Payment
          </Button>
        </div>
      </div>
    </Modal>
  );
};
